import React, { useState } from "react";
import { Col, Row, Alert } from "react-bootstrap";
import SearchBar from "./SearchBar";
import Filters from "./Filters";
import RenewPopup from "./RenewPopup";
import EditProfile from "./EditProfile";
import AttendanceCalendar from "./AttendanceCalendar";
import { getRenewalMessage } from "../../utils/utils";

function Table({ members }) {
  const [showAddMember, setShowAddMember] = useState(false);
  const [showEditMember, setShowEditMember] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [membershipFilter, setMembershipFilter] = useState("");
  const [activityFilter, setActivityFilter] = useState("");

  const [attendanceData, setAttendanceData] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const [editMemberData, setEditMemberData] = useState(null);

  const handleCloseAddMember = () => setShowAddMember(false);
  const handleShowAddMember = (member, attendance) => {
    setCurrentMember(member);
    setAttendanceData(attendance);
    setShowAddMember(true);
  };

  const handleCloseEditMember = () => setShowEditMember(false);
  const handleShowEditMember = async (MID) => {
    const data = await fetchMemberDetailsForEdit(MID);
    setEditMemberData(data);
    setShowEditMember(true);
  };

  const handleCloseCalendar = () => setShowCalendar(false);

  // -----------------------------
  // Helpers
  // -----------------------------
  const startOfDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const safeDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00") return null;
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? null : d;
  };

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  const formatDate = (dateString) => {
    if (dateString === "0000-00-00") return "No Date Present";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const calcDaysToRenewal = (renewDate) => {
    const today = startOfDay(new Date());
    const renewDay = startOfDay(renewDate);
    return Math.round(
      (renewDay.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
  };

  // -----------------------------
  // API calls
  // -----------------------------
  const fetchAttendance = async (MID) => {
    try {
      const response = await fetch(
        `https://thedynamicgym.in/api/attendancelist/${MID}`,
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      return [];
    }
  };

  const fetchMemberDetails = async (MID) => {
    try {
      const response = await fetch(
        `https://thedynamicgym.in/api/members/${MID}`,
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching member details:", error);
      return null;
    }
  };

  const fetchMemberDetailsForEdit = async (MID) => {
    try {
      const response = await fetch(
        `https://thedynamicgym.in/api/Editprofile/${MID}`,
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error fetching member details for edit:", error);
      return null;
    }
  };

  const handleFetchAndShowCalendar = async (MID) => {
    const data = await fetchAttendance(MID);
    setAttendanceData(data);
    setShowCalendar(true);
  };

  // -----------------------------
  // Absent helpers (for new filters)
  // -----------------------------
  const getAbsentDaysCount = (attendanceRecords) => {
    if (!attendanceRecords || attendanceRecords.length === 0) return null;

    const latestPresentDate = new Date(
      Math.max(...attendanceRecords.map((record) => new Date(record.Date))),
    );

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const diffTime = yesterday - latestPresentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) return 0;
    return diffDays - 1;
  };

  // Existing absent display (unchanged)
  const calculateAbsentDays = (attendanceRecords) => {
    if (!attendanceRecords || attendanceRecords.length === 0) return null;

    const latestPresentDate = new Date(
      Math.max(...attendanceRecords.map((record) => new Date(record.Date))),
    );

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const diffTime = yesterday - latestPresentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 1) return null;
    if (diffDays > 8) return "Absent - 8+ days";
    return `Absent - ${diffDays - 1} days`;
  };

  // -----------------------------
  // Default sort
  // -----------------------------
  const baseSortedMembers = [...(members || [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  // -----------------------------
  // Filter
  // -----------------------------
  let filteredMembers = baseSortedMembers.filter((member) => {
    const searchLower = searchTerm.toLowerCase();

    const matchesSearch =
      member.MID?.toString().includes(searchTerm) ||
      member.Name?.toLowerCase().includes(searchLower);

    if (!matchesSearch) return false;

    const renewDateRaw = member?.MembershipDetails?.["Renewal Date"];
    const renewDate = safeDate(renewDateRaw);

    if (!renewDate) return membershipFilter === "" && activityFilter === "";

    const daysToRenewal = calcDaysToRenewal(renewDate);

    const attendanceRecords = Array.isArray(member.AttendanceRecords)
      ? member.AttendanceRecords
      : [];

    const attendanceDates = attendanceRecords
      .map((r) => safeDate(r.Date))
      .filter(Boolean)
      .map(startOfDay);

    const today = startOfDay(new Date());
    const renewDay = startOfDay(renewDate);

    // -----------------------------
    // Filter 1: Membership
    // -----------------------------
    let membershipMatch = true;

    if (membershipFilter) {
      membershipMatch = false;

      if (membershipFilter === "renewIn3" && daysToRenewal === 3)
        membershipMatch = true;
      if (membershipFilter === "renewIn2" && daysToRenewal === 2)
        membershipMatch = true;
      if (membershipFilter === "renewIn1" && daysToRenewal === 1)
        membershipMatch = true;
      if (membershipFilter === "renewToday" && daysToRenewal === 0)
        membershipMatch = true;

      if (membershipFilter === "expired1" && daysToRenewal === -1)
        membershipMatch = true;
      if (membershipFilter === "expired2" && daysToRenewal === -2)
        membershipMatch = true;
      if (membershipFilter === "expired3" && daysToRenewal === -3)
        membershipMatch = true;

      if (membershipFilter === "expiredAll" && daysToRenewal < 0)
        membershipMatch = true;
    }

    if (!membershipMatch) return false;

    // -----------------------------
    // Filter 2: Activity
    // -----------------------------
    let activityMatch = true;

    if (activityFilter) {
      activityMatch = false;

      // Present Today
      if (activityFilter === "presentToday") {
        activityMatch = attendanceDates.some((d) => isSameDay(d, today));
      }

      // Visited After Expiry (All)
      if (activityFilter === "visitedAfterExpiry") {
        if (daysToRenewal < 0) {
          activityMatch = attendanceDates.some(
            (d) => d.getTime() > renewDay.getTime(),
          );
        } else {
          activityMatch = false;
        }
      }

      // ✅ NEW: Visited After Expiry (7 days)
      // expired + any visit AFTER expiry AND that visit is within last 7 days
      if (activityFilter === "visitedAfterExpiry7") {
        if (daysToRenewal < 0) {
          const sevenDaysAgo = new Date(today);
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 10); // last 7 days incl today

          activityMatch = attendanceDates.some(
            (d) =>
              d.getTime() > renewDay.getTime() &&
              d.getTime() >= sevenDaysAgo.getTime() &&
              d.getTime() <= today.getTime(),
          );
        } else {
          activityMatch = false;
        }
      }

      // Absent for 4 days
      if (activityFilter === "absent4") {
        const absentCount = getAbsentDaysCount(member.AttendanceRecords);
        activityMatch = absentCount === 4;
      }

      // Absent for 8 and 8+ days
      if (activityFilter === "absent8plus") {
        const absentCount = getAbsentDaysCount(member.AttendanceRecords);
        activityMatch = absentCount !== null && absentCount >= 8;
      }
    }

    return activityMatch;
  });

  // ✅ Special sorting by Renewal Date DESC (near expiry first)
  // Apply when:
  // 1) Expired (All)
  // 2) Visited After Expiry (All)
  // 3) Visited After Expiry (7 days)
  const shouldSortByRenewalDate =
    membershipFilter === "expiredAll" ||
    activityFilter === "visitedAfterExpiry" ||
    activityFilter === "visitedAfterExpiry7";

  if (shouldSortByRenewalDate) {
    filteredMembers = [...filteredMembers].sort((a, b) => {
      const ra = safeDate(a?.MembershipDetails?.["Renewal Date"]);
      const rb = safeDate(b?.MembershipDetails?.["Renewal Date"]);

      if (!ra && !rb) return 0;
      if (!ra) return 1;
      if (!rb) return -1;

      return startOfDay(rb).getTime() - startOfDay(ra).getTime();
    });
  }

  if (!members || members.length === 0) {
    return <Alert variant="warning">No members available</Alert>;
  }

  const baseURL = "https://thedynamicgym.in";

  return (
    <div>
      <div>
        <Row>
          <Col md={6} sm={12}>
            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </Col>

          <Col md={{ span: 5, offset: 1 }} sm={12}>
            <Filters
              membershipFilter={membershipFilter}
              setMembershipFilter={setMembershipFilter}
              activityFilter={activityFilter}
              setActivityFilter={setActivityFilter}
            />
          </Col>
        </Row>
      </div>

      <div className="tableclsdiv MT20">
        <table className="tablecls">
          <thead>
            <tr className="trcls">
              <th>ID</th>
              <th>Name</th>
              <th>Number</th>
              <th>Renewal Date</th>
              <th>Member</th>
              <th>Renew</th>
              <th className="textalgncenter">Attendance</th>
              <th>Edit</th>
            </tr>
          </thead>

          <tbody>
            {filteredMembers.length > 0 ? (
              filteredMembers.map((member) => (
                <tr key={member.MID}>
                  <td>{member.MID}</td>
                  <td>{member.Name}</td>
                  <td>{member.Number}</td>
                  <td>
                    {formatDate(member.MembershipDetails["Renewal Date"])}
                    <br />
                    <span
                      className={
                        getRenewalMessage(
                          member.MembershipDetails["Renewal Date"],
                        ).className
                      }
                    >
                      {
                        getRenewalMessage(
                          member.MembershipDetails["Renewal Date"],
                        ).message
                      }
                    </span>
                  </td>

                  <td className="UsernameImg">
                    <img
                      src={baseURL + "/" + member.ImagePath}
                      alt=""
                      loading="lazy"
                    />
                  </td>

                  <td>
                    <button
                      className="Rbtn"
                      onClick={async () => {
                        const attendance = await fetchAttendance(member.MID);
                        const memberDetails = await fetchMemberDetails(
                          member.MID,
                        );
                        handleShowAddMember(memberDetails, attendance);
                      }}
                    >
                      Renew
                    </button>
                  </td>

                  <td className="textalgncenter">
                    <button
                      className="iconbtns"
                      onClick={() => handleFetchAndShowCalendar(member.MID)}
                    >
                      <img src="Images/date.png" alt="Attendance" />
                      <p className="absentcls">
                        {calculateAbsentDays(member.AttendanceRecords) &&
                          calculateAbsentDays(member.AttendanceRecords)}
                      </p>
                    </button>
                  </td>

                  <td>
                    <button
                      className="iconbtns"
                      onClick={() => handleShowEditMember(member.MID)}
                    >
                      <img src="Images/edit.png" alt="Edit" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8">No members found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <RenewPopup
        show={showAddMember}
        handleClose={handleCloseAddMember}
        member={currentMember}
        attendanceData={attendanceData}
      />

      <EditProfile
        show={showEditMember}
        handleClose={handleCloseEditMember}
        memberData={editMemberData}
      />

      <AttendanceCalendar
        show={showCalendar}
        handleClose={handleCloseCalendar}
        attendanceData={attendanceData}
      />
    </div>
  );
}

export default Table;
